import { useRef, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Trans, useTranslation } from "react-i18next";
import {
  CAlert,
  CButton,
  CCard,
  CCardBody,
  CCardHeader,
  CCol,
  CForm,
  CFormFeedback,
  CFormInput,
  CFormLabel,
  COneTimePassword,
  COneTimePasswordInput,
  CRow,
  CSpinner,
  CStepper,
} from "@coreui/react-pro";
import CIcon from "@coreui/icons-react";
import { cilCheckCircle, cilWarning } from "@coreui/icons";
import type { ApiError } from "../../api/client";
import { useFailure } from "../../context/useFailure";
import {
  useCustomerProfile,
  useRequestEmailChange,
  useVerifyEmailChange,
} from "../../api/hooks/useCustomerProfile";
import { routePaths } from "../../routes/route-paths";

// CStepper's exposed imperative API. Mirrors the shape used by Signal
// Manager's onboarding wizard so the patterns stay aligned.
interface CStepperRef {
  next: () => void;
  prev: () => void;
  finish: () => void;
  reset: () => void;
}

const EMAIL_RX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
const CODE_LENGTH = 6;

// Server error codes that mean this request is dead — retrying the code can't help, so the customer is
// sent to the result step to start over. Anything else stays retryable on the verify step.
const TERMINAL_ERROR_CODES = new Set([
  "TooManyAttempts",
  "CodeExpired",
  "NoPendingRequest",
  "Duplicate",
]);

type EmailChangeResult =
  | { kind: "success"; email: string }
  | { kind: "failure"; message: string };

export function EmailChangeWizardCoreui() {
  const navigate = useNavigate();
  const { t } = useTranslation("profile");
  const profile = useCustomerProfile();
  const requestEmailChange = useRequestEmailChange();
  const verifyEmailChange = useVerifyEmailChange();

  const stepperRef = useRef<CStepperRef>(null);
  const [currentStep, setCurrentStep] = useState(1);
  const { showFailure, clearFailure } = useFailure();
  const [validated, setValidated] = useState(false);
  const [newEmail, setNewEmail] = useState("");
  const [sendError, setSendError] = useState<string | null>(null);
  const [code, setCode] = useState("");
  const [codeError, setCodeError] = useState<string | null>(null);
  const [result, setResult] = useState<EmailChangeResult | null>(null);

  const currentEmail = profile.data?.email ?? "";
  const trimmedNewEmail = newEmail.trim();
  const emailValid = EMAIL_RX.test(trimmedNewEmail);
  const isSameAsCurrent =
    trimmedNewEmail.length > 0 &&
    trimmedNewEmail.toLowerCase() === currentEmail.trim().toLowerCase();

  // Same-as-current is unambiguous the moment it's typed, so flag it live. A format error waits for a
  // submit attempt so we're not scolding someone mid-type. Server rejections (address taken, cooldown)
  // share the same slot — they're all "the problem is this field", which a toast never conveyed.
  const emailInvalid = isSameAsCurrent || !!sendError || (validated && !emailValid);
  const emailFeedback =
    sendError ??
    (isSameAsCurrent ? t("email.step1.sameAsCurrent") : t("email.step1.invalid"));

  // Any newly issued code supersedes the previous one server-side, so never carry a typed code across.
  const resetCodeState = () => {
    setCode("");
    setCodeError(null);
  };

  const handleBack = () => {
    if (currentStep === 1) {
      navigate(routePaths.profile);
      return;
    }
    setValidated(false);
    resetCodeState();
    stepperRef.current?.prev();
  };

  const handleSendCode = async () => {
    // The button is disabled for this, but guard anyway — never advance to a verify step with no
    // request behind it.
    if (isSameAsCurrent) {
      return;
    }
    if (!emailValid) {
      setValidated(true);
      return;
    }
    setSendError(null);
    clearFailure();
    try {
      await requestEmailChange.mutateAsync(trimmedNewEmail);
      setValidated(false);
      resetCodeState();
      stepperRef.current?.next();
    } catch (err) {
      const apiError = err as ApiError;
      const message = err instanceof Error ? err.message : t("email.errors.sendFailed");
      // A refusal (address taken, cooldown, bad format) is about the field right above, so it stays
      // on the field. Our own failure is not about what they typed, and it carries a reference.
      if (apiError?.status !== undefined && apiError.status < 500) {
        setSendError(message);
      } else {
        showFailure(message, apiError?.traceId);
      }
    }
  };

  // Fires from the Verify button and automatically from the OTP input once the last digit lands.
  // The isPending guard matters: a wrong code burns one of only three attempts, so we must never
  // let a double-fire spend one.
  const handleVerify = async (submitted: string) => {
    if (verifyEmailChange.isPending) {
      return;
    }
    if (submitted.length !== CODE_LENGTH) {
      setCodeError(t("email.step2.invalid"));
      return;
    }

    setCodeError(null);
    try {
      const verified = await verifyEmailChange.mutateAsync(submitted);
      setResult({ kind: "success", email: verified.email });
      stepperRef.current?.next();
    } catch (err) {
      const apiError = err as ApiError;
      const message = apiError?.message ?? t("email.errors.verifyFailed");

      if (apiError?.code && TERMINAL_ERROR_CODES.has(apiError.code)) {
        // Dead end — retrying the code can't help, so land on the result step to start over.
        setResult({ kind: "failure", message });
        stepperRef.current?.next();
      } else {
        setCodeError(message);
      }
      // Clear so onComplete can't immediately re-fire the same (now known-bad) value.
      setCode("");
    }
  };

  const handleRequestNewCode = () => {
    resetCodeState();
    setResult(null);
    setValidated(false);
    stepperRef.current?.reset();
  };

  const steps = [
    {
      label: t("email.steps.newEmail"),
      content: (
        <CCard>
          <CCardHeader>
            <h4 className="mb-0">{t("email.step1.heading")}</h4>
          </CCardHeader>
          <CCardBody>
            <p className="text-body-secondary small">{t("email.step1.info")}</p>
            <p>
              <strong>{t("email.step1.currentEmail")}</strong>{" "}
              {currentEmail || t("email.step1.notSet")}
            </p>
            <CForm noValidate>
              <CRow className="mb-4">
                <CCol md={6}>
                  <CFormLabel htmlFor="new-email">
                    {t("email.step1.label")}
                  </CFormLabel>
                  <CFormInput
                    id="new-email"
                    type="email"
                    value={newEmail}
                    onChange={(e) => {
                      setNewEmail(e.target.value);
                      // A server rejection applied to the old value — drop it as soon as they edit.
                      if (sendError) {
                        setSendError(null);
                      }
                    }}
                    invalid={emailInvalid}
                    placeholder={t("email.step1.placeholder")}
                  />
                  <CFormFeedback invalid>{emailFeedback}</CFormFeedback>
                </CCol>
              </CRow>
            </CForm>
            <div className="d-flex justify-content-between">
              <CButton
                color="secondary"
                variant="outline"
                onClick={handleBack}
                disabled={requestEmailChange.isPending}
              >
                {t("email.step1.cancel")}
              </CButton>
              <CButton
                color="primary"
                onClick={handleSendCode}
                disabled={requestEmailChange.isPending || isSameAsCurrent}
              >
                {requestEmailChange.isPending && (
                  <CSpinner size="sm" className="me-2" />
                )}
                {t("email.step1.send")}
              </CButton>
            </div>
          </CCardBody>
        </CCard>
      ),
    },
    {
      label: t("email.steps.verify"),
      content: (
        <CCard>
          <CCardHeader>
            <h4 className="mb-0">{t("email.step2.heading")}</h4>
          </CCardHeader>
          <CCardBody>
            <div className="text-center py-2">
              <p className="text-body-secondary mx-auto" style={{ maxWidth: 480 }}>
                <Trans
                  i18nKey="email.step2.info"
                  ns="profile"
                  values={{ email: trimmedNewEmail }}
                  components={{ 1: <strong /> }}
                />
              </p>

              <div className="d-flex flex-column align-items-center py-3">
                <CForm noValidate>
                  <COneTimePassword
                    id="verify-code"
                    type="number"
                    size="lg"
                    value={code}
                    onChange={(value) => {
                      setCode(value);
                      // Clear the error as soon as they start a fresh attempt.
                      if (codeError) {
                        setCodeError(null);
                      }
                    }}
                    onComplete={handleVerify}
                    invalid={!!codeError}
                    disabled={verifyEmailChange.isPending}
                  >
                    {Array.from({ length: CODE_LENGTH }, (_, index) => (
                      <COneTimePasswordInput key={index} />
                    ))}
                  </COneTimePassword>
                </CForm>
              </div>

              {/* Rendered explicitly rather than via feedbackInvalid: Bootstrap hides .invalid-feedback
                  unless it sits next to a matching .is-invalid sibling, so inside the OTP wrapper the
                  message never showed — you only got red boxes and no explanation. */}
              {codeError && (
                <CAlert
                  color="danger"
                  className="py-2 mx-auto mb-3"
                  style={{ maxWidth: 480 }}
                >
                  {codeError}
                </CAlert>
              )}

              <p className="text-body-secondary small mb-0">
                {t("email.step2.resendHint")}
              </p>
            </div>

            <div className="d-flex justify-content-between mt-3">
              <CButton
                color="secondary"
                variant="outline"
                onClick={handleBack}
                disabled={verifyEmailChange.isPending}
              >
                {t("email.step2.back")}
              </CButton>
              <CButton
                color="primary"
                onClick={() => handleVerify(code)}
                disabled={
                  verifyEmailChange.isPending || code.length !== CODE_LENGTH
                }
              >
                {verifyEmailChange.isPending && (
                  <CSpinner size="sm" className="me-2" />
                )}
                {t("email.step2.verify")}
              </CButton>
            </div>
          </CCardBody>
        </CCard>
      ),
    },
    {
      label: t("email.steps.done"),
      content: (
        <CCard>
          <CCardHeader>
            <h4 className="mb-0">
              {result?.kind === "failure"
                ? t("email.step3.failureHeading")
                : t("email.step3.successHeading")}
            </h4>
          </CCardHeader>
          <CCardBody>
            {result?.kind === "failure" ? (
              <>
                <div className="text-center py-3 mb-2">
                  <CIcon icon={cilWarning} size="3xl" className="text-warning mb-3" />
                  <h3 className="mb-2">{t("email.step3.failureTitle")}</h3>
                  <p
                    className="text-body-secondary mx-auto"
                    style={{ maxWidth: 480 }}
                  >
                    {result.message}
                  </p>
                </div>
                <div className="d-flex flex-wrap gap-2 justify-content-center">
                  <CButton color="primary" onClick={handleRequestNewCode}>
                    {t("email.step3.requestNewCode")}
                  </CButton>
                  <Link
                    to={routePaths.profile}
                    className="btn btn-outline-secondary"
                  >
                    {t("email.step3.backToProfile")}
                  </Link>
                </div>
              </>
            ) : (
              <>
                <div className="text-center py-3 mb-2">
                  <CIcon
                    icon={cilCheckCircle}
                    size="3xl"
                    className="text-success mb-3"
                  />
                  <h3 className="mb-2">{t("email.step3.successTitle")}</h3>
                  <p
                    className="text-body-secondary mx-auto"
                    style={{ maxWidth: 480 }}
                  >
                    {t("email.step3.successBody", { email: result?.email ?? "" })}
                  </p>
                </div>
                <div className="d-flex flex-wrap gap-2 justify-content-center">
                  <Link to={routePaths.profile} className="btn btn-primary">
                    {t("email.step3.backToProfile")}
                  </Link>
                </div>
              </>
            )}
          </CCardBody>
        </CCard>
      ),
    },
  ];

  return (
    <>
      <h2>{t("email.title")}</h2>
      <p className="text-body-secondary">{t("email.subtitle")}</p>
      <hr />

      <CStepper
        ref={stepperRef}
        steps={steps}
        onStepChange={setCurrentStep}
        onReset={() => setCurrentStep(1)}
      />
    </>
  );
}
